import { type ComponentProps, useMemo, useRef, useState } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { getTranslatedCountry, type Locale } from '@worldcoo/monolith/core';
import { countries } from '@worldcoo/monolith/forms/countries';
import {
	Combobox,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxItem,
	ComboboxLabel,
	ComboboxList,
	ComboboxSeparator,
	ComboboxValue,
} from '../combobox';
import { Flag, countryCodeToLocale } from './country-flags';
import { cn } from '../../lib/utils';
import type { CountryFieldTexts } from './country-field';

type CountryOption = { value: string; label: string };

const priorityCountries = countries.filter((c) => c.priority === 1);
const nonPriorityCountries = countries.filter((c) => c.priority >= 2);

function CountryFlag({ code }: { code: string }) {
	const locale = countryCodeToLocale[code];
	if (!locale) return null;
	return <Flag locale={locale} className="size-5 shrink-0" />;
}

export default function MultiCountryField({
	label,
	locale = 'en',
	className,
	texts,
}: ComponentProps<'div'> & {
	label: string;
	locale?: Locale;
	texts: CountryFieldTexts;
}) {
	const field = useFieldContext<string[]>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name);
	const [searchValue, setSearchValue] = useState('');
	const chipsRef = useRef<HTMLDivElement>(null);

	const priorityItems = useMemo<CountryOption[]>(
		() =>
			priorityCountries.map((c) => ({
				value: c.country,
				label: getTranslatedCountry(c.country, locale) ?? c.name,
			})),
		[locale],
	);

	const otherItems = useMemo<CountryOption[]>(
		() =>
			nonPriorityCountries.map((c) => ({
				value: c.country,
				label: getTranslatedCountry(c.country, locale) ?? c.name,
			})),
		[locale],
	);

	const allItems = useMemo(() => [...priorityItems, ...otherItems], [priorityItems, otherItems]);

	const selectedItems = useMemo(
		() => allItems.filter((i) => field.state.value.includes(i.value)),
		[allItems, field.state.value],
	);

	const labelByValue = useMemo(() => new Map(allItems.map((o) => [o.value, o.label])), [allItems]);

	const filteredPriorityItems = useMemo(() => {
		if (!searchValue) return priorityItems;
		const search = searchValue.toLowerCase();
		return priorityItems.filter((item) => item.label.toLowerCase().includes(search));
	}, [priorityItems, searchValue]);

	const filteredOtherItems = useMemo(() => {
		if (!searchValue) return otherItems;
		const search = searchValue.toLowerCase();
		return otherItems.filter((item) => item.label.toLowerCase().includes(search));
	}, [otherItems, searchValue]);

	return (
		<div
			className={cn(
				'flex flex-col gap-2',
				className,
				formReadOnly &&
					'pointer-events-none [&_[data-slot=combobox-chips]]:border-transparent [&_[data-slot=combobox-chips]]:px-0 [&_[data-slot=combobox-chips]]:focus-within:ring-0 [&_[data-slot=combobox-chip-input]]:min-w-0 [&_[data-slot=combobox-chip-input]]:flex-[0] [&_[data-slot=combobox-chip-input]]:opacity-0 [&_[data-slot=combobox-chip-input]]:overflow-hidden [&_[data-slot=combobox-chip-remove]]:opacity-0 [&_[data-slot=combobox-chip-remove]]:w-0 [&_[data-slot=combobox-chip-remove]]:-ml-2 [&_[data-slot=combobox-chip]]:px-0 [&_[data-slot=combobox-chip]]:h-auto',
			)}
		>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<Combobox
				modal
				multiple
				items={allItems}
				value={selectedItems}
				onValueChange={(newItems: CountryOption[]) => {
					field.handleChange(newItems.map((item) => item.value));
				}}
				onInputValueChange={setSearchValue}
				isItemEqualToValue={(a: CountryOption, b: CountryOption) => a.value === b.value}
			>
				<ComboboxChips ref={chipsRef}>
					<ComboboxValue>
						{field.state.value.filter(Boolean).map((v) => (
							<ComboboxChip key={v} className="bg-transparent">
								<CountryFlag code={v} />
								{labelByValue.get(v) ?? v}
							</ComboboxChip>
						))}
					</ComboboxValue>
					<ComboboxChipsInput id={field.name} aria-invalid={hasFieldError(field.state.meta)} placeholder={label} />
				</ComboboxChips>
				<ComboboxContent anchor={chipsRef}>
					<ComboboxList>
						{filteredPriorityItems.length > 0 && (
							<ComboboxGroup>
								<ComboboxLabel>{texts.popular}</ComboboxLabel>
								{filteredPriorityItems.map((item) => (
									<ComboboxItem key={item.value} value={item}>
										<CountryFlag code={item.value} />
										{item.label}
									</ComboboxItem>
								))}
							</ComboboxGroup>
						)}
						{filteredPriorityItems.length > 0 && filteredOtherItems.length > 0 && <ComboboxSeparator />}
						{filteredOtherItems.length > 0 && (
							<ComboboxGroup>
								<ComboboxLabel>{texts.allCountries}</ComboboxLabel>
								{filteredOtherItems.map((item) => (
									<ComboboxItem key={item.value} value={item}>
										<CountryFlag code={item.value} />
										{item.label}
									</ComboboxItem>
								))}
							</ComboboxGroup>
						)}
					</ComboboxList>
					<ComboboxEmpty>{texts.noCountryFound}</ComboboxEmpty>
				</ComboboxContent>
			</Combobox>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
